import {capitalize, Stack, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';

import {APIError} from 'data/errors/api-error';
import {HttpStatusCode} from 'data/protocols/http';
import {GetNeighborhoodsDentalGuideParams, TypeLocalNameEnum} from 'domain/usecases';
import {FinishWithoutCompleteFlowEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {OdontoGuideServicesTypeEnum} from 'presentation/constants';
import {
	BookingEmptyState,
	BookingErrorState,
	BookingLoadingState,
	BookingNotFoundListCard,
	BookingNotFoundState,
} from 'presentation/pages/private/booking/components';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {BookingNotFoundStateVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundState/types';
import {FlowEnum, PATH_TITLE} from 'presentation/pages/private/booking/constants';
import {useGetOdontoDistrictsQuery} from 'presentation/pages/private/booking/queries/useGetOdontoDistrictsQuery';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {parseCommaSeparated} from '../../utils/parseCommaSeparated';
import {DistrictList} from '../DistrictList';
import {OdontoDistrictsProps} from './types';

const {
	loadingError,
	loadingState,
	emptySearchState,
	emptyModal: {part1, part2},
} = translations['pt-br'].privateFlow.bookingFlow.districtFlow;
const {booking} = translations['pt-br'].others.sidebar;
const {all, goForward, selectAll, notice, understood} = translations['pt-br'].commons;
const {cardListText} = translations['pt-br'].privateFlow.bookingFlow.districtFlow.notFoundModal;

export function OdontoDistricts({handlePrevStep, handleNextStep}: OdontoDistrictsProps) {
	const {isMobile} = useMediaQueries();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {
		data: healthCard,
		isLoading: isLoadingHealthCard,
		isFetching: isFetchingHealthCard,
	} = useGetHealthCardQuery();
	const {SEARCH_PARAMS} = useQueryParams();
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: booking,
	});

	const {query} = SEARCH_PARAMS;
	const cdPlanoANS =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns || '';

	const districtParams: GetNeighborhoodsDentalGuideParams = {
		cdEspecialidade: String(bookingData.specialtyCode),
		nmCidade: bookingData.cityName,
		cdUf: bookingData.ufCode,
		cdPlanoANS,
		dsServico: OdontoGuideServicesTypeEnum.OUTPATIENT,
		tpLocalidade: capitalize(TypeLocalNameEnum.CIDADE.toLowerCase()),
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetOdontoDistrictsQuery({params: districtParams});

	const isLoading = districtListIsLoading || isLoadingHealthCard || isFetchingHealthCard;

	const districtListHasUnprocessableError =
		districtListHasError &&
		districtListError instanceof APIError &&
		districtListError.statusCode === HttpStatusCode.unprocessable;

	const NOT_FOUND =
		!districtList ||
		districtList.length === 0 ||
		(districtListHasError && districtListHasUnprocessableError);

	useEffect(() => {
		setHideSearchBarState(NOT_FOUND);
	}, [setHideSearchBarState, NOT_FOUND, districtList, districtListIsLoading]);

	if (districtListHasError && !districtListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrevStep}
				handleRefresh={refetch}
			/>
		);
	}

	if (isLoading) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrevStep}
			/>
		);
	}

	if (NOT_FOUND) {
		return (
			<BookingNotFoundState
				showHeader={false}
				flowType={FlowEnum.ODONTO}
				pageTitle={pageTitle}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.DISTRICT}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_DISPONIVEL}
			/>
		);
	}

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			let updatedSelected: string[];

			if (prevSelected.includes(district)) {
				updatedSelected = prevSelected.filter(item => item !== district);
			} else {
				updatedSelected = [...prevSelected, district];
			}

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const FILTERED_DISTRICT_LIST = districtList.filter(districtItem =>
		districtItem.district.toLowerCase().includes(query.trim().toLowerCase()),
	);

	const showEmptyState = FILTERED_DISTRICT_LIST.length === 0;
	if (showEmptyState) {
		return (
			<BookingEmptyState
				description={emptySearchState.emptyDistrictAll}
				flowType={FlowEnum.ODONTO}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_ENCONTRADO}
			/>
		);
	}

	return (
		<Stack gap={2}>
			<DistrictList
				list={FILTERED_DISTRICT_LIST}
				handleSelectCard={handleSelectCard}
				selectedDistricts={selectedDistricts}
			/>

			<Stack mt={2} id="go-forward-button">
				<BookingNotFoundListCard
					flowType={FlowEnum.ODONTO}
					customOrientationText={cardListText}
					variant={BookingNotFoundVariantEnum.DISTRICT}
					openModalDataTagId={
						OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_BAIRRO_DESEJADO_ODONTO
					}
					modalHeaderModalDataTagId={
						OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO_ODONTO
					}
					modalCloseModalDataTagId={
						OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO_ODONTO
					}
					modalFirstActionDataTagId={
						OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO_ODONTO
					}>
					<CTAButtonFixed.Root>
						<CTAButtonFixed.Action
							fullWidth
							onClick={handleGoForward}
							data-tag-id={
								OdontoBookingTaggingEnum.CLICK_AVANCAR_SELECAO_BAIRROS_ODONTO
							}
							size="large">
							{goForward}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</BookingNotFoundListCard>
			</Stack>
			{showEmptyDistrictsModal ? (
				<AlertModal
					open={showEmptyDistrictsModal}
					headerTitle={notice}
					variant="info"
					onClose={() => setShowEmptyDistrictsModal(false)}
					buttonLabel={understood}
					slotProps={{
						paper: {
							style: {
								maxWidth: 420,
								width: '100%',
							},
						},
					}}
					subDescription={
						<Typography variant="h6" fontSize={18} textAlign="center" fontWeight={600}>
							{part1} <br /> {part2}
						</Typography>
					}
				/>
			) : null}
		</Stack>
	);
}
